import {NgOptimizedImage, NgTemplateOutlet} from '@angular/common';
import {Component, Input, OnInit, TemplateRef} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell, MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef, MatNoDataRow,
    MatRow,
    MatRowDef,
    MatTable
} from '@angular/material/table';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {TranslatePipe} from '@ngx-translate/core';
import {ProductModel, ProductPhotoModel, ProductTableModel} from '../../../models/products.model';
import {MemberModel} from '../../../models/user.model';
import {AlertService} from '../../../services/notify/alert.service';
import {ProductionService} from '../../../services/production.service';
import {StatisticService} from '../../../services/statistic.service';
import {ComponentsModule} from '../../components.module';

type TabContent = ('SERVICE' | 'PRODUCT' | 'STORAGE');
type CardsStatistic = {
    product: number,
    service: number,
    warehouse: number,
}

@Component({
    selector: 'app-products-index-tab',
    standalone: true,
    imports: [
        NgOptimizedImage,
        ComponentsModule,
        MatCell,
        MatCellDef,
        MatColumnDef,
        MatHeaderCell,
        MatHeaderRow,
        MatHeaderRowDef,
        MatRow,
        MatRowDef,
        MatTable,
        NgTemplateOutlet,
        TranslatePipe,
        MatHeaderCellDef,
        MatNoDataRow
    ],
    templateUrl: './products-index-tab.component.html',
    styleUrl: './products-index-tab.component.scss'
})
export class ProductsIndexTabComponent implements OnInit {
    @Input() currentMember: MemberModel;

    tab: TabContent = 'PRODUCT';

    products: ProductTableModel[];
    copyProducts: ProductTableModel[];
    services: ProductTableModel[];
    copyServices: ProductTableModel[];

    selectedCard: ProductModel;
    activeBigPhoto: ProductPhotoModel;

    statisticData: CardsStatistic;

    constructor(
        private productService: ProductionService,
        private alertService: AlertService,
        private sanitizer: DomSanitizer,
        private dialog: MatDialog,
        private statistic: StatisticService
    ) {
        this.getStatistic();
    }

    ngOnInit() {
        console.log(this.currentMember);

        this.getCards(this.tab);
    }

    getStatistic(): void {
        this.statistic.getIndexPageStatistics('products').subscribe({
            next: res => {
                this.statisticData = res.products as CardsStatistic;
            }
        });
    }

    getCards(type: TabContent): void {
        this.tab = type;
        this.productService.getLastHundredProduction(type as ('SERVICE' | 'PRODUCT')).subscribe({
            next: res => {
                if (type === 'SERVICE') {
                    this.services = res.products;
                    this.copyServices = structuredClone(res.products);
                } else {
                    this.products = res.products;
                    this.copyProducts = structuredClone(res.products);
                }
                this.getFirstImages(type);
                console.log(res);
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
    }

    private getFirstImages(type: TabContent): void {
        const data = type === 'SERVICE' ? this.services : this.products;

        data.forEach((card: ProductTableModel) => {
            if (card.photo_first) {
                this.productService.getPhotoInProductCard(card.puid, card.photo_first.ukey).subscribe({
                    next: res => {
                        card.photo_first.originImage = this.convertBlobToImage(res);
                        if (type === 'SERVICE') {
                            this.copyServices.find(x => x.product_card.puid === card.puid)
                                .photo_first.originImage = this.convertBlobToImage(res);
                        } else {
                            this.copyProducts.find(x => x.product_card.puid === card.puid)
                                .photo_first.originImage = this.convertBlobToImage(res);
                        }
                    }, error: err => {
                        this.alertService.setAlert(err.message, 'blue');
                    }
                });
            }
        });
    }

    private convertBlobToImage(blob: Blob): SafeResourceUrl {
        return this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob));
    }

    openCardModal(card: ProductTableModel, modalTemplate: TemplateRef<any>): void {
        this.productService.getProductCard(card.puid).subscribe({
            next: res => {
                this.selectedCard = res.product;

                if (this.selectedCard.photo_first) {
                    this.selectedCard.photo_first = structuredClone(card.photo_first);
                    this.activeBigPhoto = this.selectedCard.photo_first;
                    this.selectedCard.photos.forEach(image => {
                        this.productService.getPhotoInProductCard(card.puid, image.ukey).subscribe({
                            next: res => {
                                image.originImage = this.convertBlobToImage(res);
                            },
                            error: err => {
                                this.alertService.setAlert(err.message.info, 'blue');
                            }
                        });
                    });
                } else {
                    this.activeBigPhoto = null;
                }
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
        this.dialog.open(modalTemplate, {
            minWidth: '700px',
        });
    }

    nextPhoto(): void {
        const currentImageIndex = this.selectedCard.photos.indexOf(this.selectedCard.photos.find(x => x.ukey === this.activeBigPhoto.ukey));
        if (currentImageIndex + 1 === this.selectedCard.photos.length) {
            this.activeBigPhoto = this.selectedCard.photos[0];
        } else {
            this.activeBigPhoto = this.selectedCard.photos[currentImageIndex + 1];
        }
    }

    prevPhoto(): void {
        const currentImageIndex = this.selectedCard.photos.indexOf(this.selectedCard.photos.find(x => x.ukey === this.activeBigPhoto.ukey));
        if (currentImageIndex === 0) {
            this.activeBigPhoto = this.selectedCard.photos.at(-1);
        } else {
            this.activeBigPhoto = this.selectedCard.photos[currentImageIndex - 1];
        }
    }

    searchInProducts(text: string) {
        console.log(structuredClone(this.products));
        if (text) {
            this.products = structuredClone(this.copyProducts);

            this.products = this.filterCardsBySearch(this.products, text);
        } else {
            console.log(this.copyProducts);
            this.products = structuredClone(this.copyProducts);
        }
    }

    searchInServices(text: string) {
        if (text) {
            this.services = structuredClone(this.copyServices);

            this.services = this.filterCardsBySearch(this.services, text);
        } else {
            this.services = structuredClone(this.copyServices);
        }
    }

    private filterCardsBySearch(data: ProductTableModel[], searchText: string): ProductTableModel[] {
        let filteredData: ProductTableModel[];

        const association = (data: string, searchFromStart = false) => {
            if (searchFromStart) {
                return data.toLowerCase().indexOf(searchText.toLowerCase()) === 0;
            }
            return data.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
        };

        if (searchText) {
            filteredData = data.filter(item => {
                const searchFieldsValue = item.product_card.description + item.product_card.title;

                if (association(searchFieldsValue)) {
                    return item;
                } else {
                    return null;
                }
            });
            return filteredData;
        } else {
            return data;
        }
    }

}
